import { FC, useEffect, useState } from "react";
import Select, { Theme } from "react-select";
import "./ChatButton.css";
import { Five9AllConfig } from "./config/five9-typings";
import { PrivacyChatDisclaimer, localeContentMap } from "./PrivacyChatDisclaimer";

type valueLabel = {
  value: string;
  label: string;
};

const languages: valueLabel[] = [
  { value: "UK_Chat Czech", label: "Czech" },
  { value: 'UK_Chat Dutch', label: 'Dutch' },
  { value: 'UK_Chat English', label: 'English' },
  { value: 'UK_Chat German', label: 'German' },
  { value: 'UK_Chat Romanian', label: 'Romanian' },
  { value: 'UK_Chat Russian', label: 'Russian' },
  { value: 'UK_Chat Spanish', label: 'Spanish' },
];

export interface ChatButtonProps {
  config: Five9AllConfig;
  onLanguageSelected: (profile: string, config: Five9AllConfig, isAutoSelect: boolean) => void;
}

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() ?? null;
  return null;
};

const setCookie = (name: string, value: string, days = 365): void => {
  const expires = new Date(Date.now() + days * 86400000).toUTCString();
  document.cookie = `${name}=${value}; path=/; expires=${expires}`;
};

export const ChatButton: FC<ChatButtonProps> = ({ config, onLanguageSelected }) => {
  const [hideFrame, setHideFrame] = useState<boolean>(true);
  const defaultLanguage = config.defaultProfile ? languages.find(x => x.value == config.defaultProfile) : null;

  const showLanguageSelector = config.defaultProfile ? true : false;
  const [selectedLanguage, setSelectedLanguage] = useState<valueLabel | null>(defaultLanguage ?? null);
  const [isChecked, setIsChecked] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const profile = config.profiles;
  const localeContent = localeContentMap[profile];
  const hasLocale = !!localeContent;
  const cookieKey = "privacyYlChatAccepted";

  useEffect(() => {
    const hasAccepted = getCookie(cookieKey) === "true";
    setAccepted(hasAccepted);

    const shouldAutoSelectLanguage = (!showLanguageSelector && !shouldShowDisclaimer) || (hasAccepted && !showLanguageSelector);

    if (shouldAutoSelectLanguage) {
      onLanguageSelected('', config, true);
    }
  }, [hasLocale, profile, config, onLanguageSelected]);

  const handleLaunch = () => {
    if (hasLocale) {
      setCookie(cookieKey, "true");
    }
    const chosenProfile = selectedLanguage?.value ?? '';
    onLanguageSelected(chosenProfile, config, false);
  };

  const shouldShowDisclaimer = hasLocale && !accepted;
  const isButtonDisabled = (shouldShowDisclaimer && !isChecked);

  return (
    <>
      <div className="language-header">
        <div className="five9-chat-button" onClick={() => setHideFrame(false)}>
          <span className="five9-icon"></span>
          <span className="five9-text">Chat</span>
        </div>
      </div>
      <div id="language-popup" className="language-global" hidden={hideFrame}>
        <div className="language-header">
          <div className="five9-chat-button">
            <div className="five9-icon"></div>
            <div className="five9-text">Chat</div>
            <div id="five9-minimize-icon" title="Click to minimize" onClick={() => setHideFrame(true)}></div>
          </div>
        </div>

        <div>        
            <div>
              <div id="language-title">{shouldShowDisclaimer ? localeContent.title : "Language Selector"}</div>
              <div id="language-container">         
                {showLanguageSelector && (<Select
                  defaultValue={selectedLanguage}
                  onChange={setSelectedLanguage}
                  options={languages}
                  maxMenuHeight={140}
                  isSearchable={false}
                  theme={(theme: Theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary25: theme.colors.neutral10,
                      primary: "black",
                    },
                  })}
                />)}
                {shouldShowDisclaimer && (
                <PrivacyChatDisclaimer
                  profile={profile}
                  isChecked={isChecked}
                  setIsChecked={setIsChecked}
                />
              )}
              <button
                id="language-button"
                disabled={isButtonDisabled}
                className="five9-action-btn ui-btn"
                onClick={handleLaunch}
              >
                {localeContent?.buttonText || "Launch Chat"}
              </button>
              </div>            
            </div>
        </div>
      </div>
    </>
  );
};
